/**
 * 스마트 안전장비 14종 (국토교통부·국토안전관리원
 * 「스마트 안전장비 활용 가이드라인」(2026.05 개정) 단위장비 기준)
 */
import {
  buildSmartEquipmentManagementCost,
  SMART_ITEM_COST_OVERRIDES,
} from './smartEquipmentManagementCost';
export const SMART_EQUIPMENT_MAJOR_GROUPS = [
  { id: 'monitoring', label: '통합 모니터링' },
  { id: 'alarm', label: '위험 감지·경보' },
  { id: 'worker', label: '근로자 보호' },
];

export const SMART_EQUIPMENT_ALARM_SUBGROUPS = [
  { id: 'workEnv', label: '작업환경' },
  { id: 'structureEquip', label: '구조물·장비' },
  { id: 'remoteObs', label: '원격 관측' },
];

/** @type {import('./safetyMaterialsItems').MaterialItem[]} */
const smartEquipmentItemsRaw = [
  {
    id: 'safetyManagementSystem',
    name: '안전관리 시스템',
    description: '현장 안전·위험 정보를 한곳에서 모아 보는 통합 플랫폼',
    keywords: ['안전관리시스템', '스마트안전', '통합모니터링', 'IoT', '안전플랫폼'],
    majorCategory: 'monitoring',
  },
  {
    id: 'aiCctv',
    name: 'AI CCTV',
    description: '영상 AI로 추락·끼임·위험행동을 감지·경보하는 CCTV',
    keywords: ['AI CCTV', '지능형CCTV', '영상분석', '추락감지', '스마트CCTV'],
    majorCategory: 'alarm',
    alarmSubgroup: 'workEnv',
  },
  {
    id: 'wearableCamera',
    name: '웨어러블 카메라',
    description: '근로자 착용형 영상 기록·실시간 전송',
    keywords: ['웨어러블카메라', '바디캠', '착용형카메라', '영상기록'],
    majorCategory: 'alarm',
    alarmSubgroup: 'workEnv',
  },
  {
    id: 'hazardousAreaAccess',
    name: '위험지역 접근경보',
    description: '위험구역 무단 접근 시 즉시 경보',
    keywords: ['접근경보', '위험지역', '출입통제', '비콘', 'UWB', 'RFID'],
    majorCategory: 'alarm',
    alarmSubgroup: 'workEnv',
  },
  {
    id: 'fireDetection',
    name: '화재위험 감지경보',
    description: '화재·연기·온도 이상 조기 감지·경보',
    keywords: ['화재감지', '연기감지', '화재경보', '열감지', '자동화재감지'],
    majorCategory: 'alarm',
    alarmSubgroup: 'workEnv',
  },
  {
    id: 'gasDetection',
    name: '유해가스 측정경보',
    description: '산소·유해가스 농도 실시간 측정·경보',
    keywords: ['유해가스', '가스측정', '산소농도', '질식', '가스경보'],
    majorCategory: 'alarm',
    alarmSubgroup: 'workEnv',
  },
  {
    id: 'collapseDisplacement',
    name: '붕괴·변위 위험경보',
    description: '가설·지반·구조물 변위·침하 모니터링',
    keywords: ['붕괴', '변위', '계측', '지반침하', '균열감시', 'Smart 계측'],
    majorCategory: 'alarm',
    alarmSubgroup: 'structureEquip',
  },
  {
    id: 'machineryApproach',
    name: '건설기계 접근감지',
    description: '건설기계와 근로자 접근 시 경보',
    keywords: ['건설기계', '접근감지', '협착', '중장비', '근접경보'],
    majorCategory: 'alarm',
    alarmSubgroup: 'structureEquip',
  },
  {
    id: 'concreteCuring',
    name: '콘크리트 양생 모니터링',
    description: '타설 콘크리트 온도·강도 원격 모니터링',
    keywords: ['콘크리트', '양생', '온도계측', '강도', '양생모니터링'],
    majorCategory: 'alarm',
    alarmSubgroup: 'structureEquip',
  },
  {
    id: 'remoteWireInspection',
    name: '원격 와이어 점검',
    description: '와이어로프·지보재 등 주요 부재 원격 점검',
    keywords: ['와이어로프', '원격점검', '로프검사', '지보재'],
    majorCategory: 'alarm',
    alarmSubgroup: 'structureEquip',
  },
  {
    id: 'towerCraneAntiCollision',
    name: '타워크레인 충돌방지',
    description: '타워크레인 간·장애물 충돌 방지·경보',
    keywords: ['타워크레인', '충돌방지', 'Anti-collision', '크레인안전'],
    majorCategory: 'alarm',
    alarmSubgroup: 'structureEquip',
  },
  {
    id: 'remoteSafetyObservation',
    name: '원격 안전관측(로봇/드론)',
    description: '드론·로봇으로 위험구역 원격 관측',
    keywords: ['드론', '로봇', '원격관측', '무인점검', 'UAV'],
    majorCategory: 'alarm',
    alarmSubgroup: 'remoteObs',
  },
  {
    id: 'smartAirbagVest',
    name: '스마트 에어백 조끼',
    description: '추락·충격 감지 시 에어백 전개',
    keywords: ['에어백조끼', '스마트조끼', '추락방지', '에어백'],
    majorCategory: 'worker',
  },
  {
    id: 'workerWearableSmart',
    name: '근로자 착용형 스마트 안전장비',
    description: '스마트 헬멧·통신조끼·생체신호 등 착용형 장비',
    keywords: ['스마트헬멧', '통신조끼', '착용형', '생체신호', '위치추적', 'SOS'],
    majorCategory: 'worker',
  },
];

export const smartEquipmentItems = smartEquipmentItemsRaw.map((item) => ({
  ...item,
  managementCost: buildSmartEquipmentManagementCost(SMART_ITEM_COST_OVERRIDES[item.id]),
}));

export const getSmartItemById = (id) => smartEquipmentItems.find((item) => item.id === id);

export const groupSmartItemsByCategory = (items = smartEquipmentItems) =>
  SMART_EQUIPMENT_MAJOR_GROUPS.map((group) => {
    const groupItems = items.filter((item) => item.majorCategory === group.id);
    if (group.id !== 'alarm') {
      return { ...group, items: groupItems };
    }
    const subgroups = SMART_EQUIPMENT_ALARM_SUBGROUPS.map((sub) => ({
      ...sub,
      items: groupItems.filter((item) => item.alarmSubgroup === sub.id),
    })).filter((sub) => sub.items.length > 0);
    return { ...group, items: groupItems, subgroups };
  }).filter((group) => group.items.length > 0);
